import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { createTestLink, listTestLinks, revokeTestLink, TestLink, TestLinkAttempt } from "../api/testLinks";
import { formatLocalDateTime } from "../utils/datetime";
import { ShareLinkDialog } from "./ShareLinkDialog";

const STATUS_CHIP: Record<TestLink["status"], { label: string; color: "default" | "info" | "success" | "warning" | "error" }> = {
  pending: { label: "Not started", color: "info" },
  in_progress: { label: "In progress", color: "warning" },
  completed: { label: "Completed", color: "success" },
  revoked: { label: "Revoked", color: "default" },
  expired: { label: "Expired", color: "default" },
};

export function attemptLabel(a: TestLinkAttempt): string {
  if (a.status === "completed") return "Completed";
  if (a.status === "in_progress") return "In progress";
  return a.blocks_uploaded.length
    ? `Interrupted (${a.blocks_uploaded.join(", ")})`
    : "Interrupted (nothing recorded)";
}

export async function startTestNow(patientId: string, navigate: (to: string) => void) {
  const link = await createTestLink(patientId, "supervised");
  navigate(`/test/${link.token}`);
}

export default function TestLinksSection({ patientId, onStartNow }: { patientId: string; onStartNow: (token: string) => void }) {
  const [links, setLinks] = useState<TestLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [shared, setShared] = useState<TestLink | null>(null);

  const load = useCallback(async () => {
    try {
      setLinks(await listTestLinks(patientId));
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load links");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try { await fn(); } catch (err: any) {
      setError(err?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const create = () => run(async () => {
    const link = await createTestLink(patientId, "remote");
    setLinks((prev) => [link, ...prev]);
    setShared(link);
  });
  const startNow = () => run(async () => {
    const link = await createTestLink(patientId, "supervised");
    onStartNow(link.token);
  });
  const revoke = (token: string) => {
    if (!window.confirm("Revoke this link? It will stop working immediately.")) return;
    run(async () => {
      const updated = await revokeTestLink(token);
      setLinks((prev) => prev.map((l) => (l.token === token ? updated : l)));
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Run a test</Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <Button fullWidth size="large" variant="contained" startIcon={<PlayArrowIcon />} disabled={busy} onClick={startNow}>
          Start test now
        </Button>
        <Button fullWidth size="large" variant="outlined" startIcon={<LinkIcon />} disabled={busy} onClick={create}>
          Create test link
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={24} /></Box>
      ) : links.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Test links</Typography>
          {links.map((l) => {
            const chip = STATUS_CHIP[l.status];
            const open = l.status === "pending" || l.status === "in_progress";
            return (
              <Box key={l.token} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={chip.label} color={chip.color} size="small" variant="outlined" />
                  <Chip label={l.mode === "supervised" ? "Supervised" : "Remote"} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    {formatLocalDateTime(l.created_at)} · {l.created_by_name || l.created_by_email || "—"}
                    {open ? ` · expires ${formatLocalDateTime(l.expires_at)}` : ""}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {open && <Button size="small" onClick={() => setShared(l)}>Share</Button>}
                  {open && <Button size="small" color="error" onClick={() => revoke(l.token)}>Revoke</Button>}
                </Box>
                {l.attempts.map((a, i) => (
                  <Typography key={a.scan_id} variant="caption" component="div" sx={{ mt: 0.5, pl: 0.5 }}>
                    Attempt {i + 1} · {formatLocalDateTime(a.started_at)} · {attemptLabel(a)}
                  </Typography>
                ))}
              </Box>
            );
          })}
        </Stack>
      )}
      <ShareLinkDialog link={shared} onClose={() => setShared(null)} />
    </Paper>
  );
}
